import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useModal } from './contexts/ModalContext';
import { Typography } from '@mui/material';
import ConfirmationModal from './components/modals/ConfirmationModal';
import { APP_EVENT } from './constants/events';
import { getItemFromSessionStorage, setItemInSessionStorage } from './utils/storageHelper';

interface RequireTenantGuardProps {
  children: ReactNode;
}

interface Tenant {
  id: string;
  name: string;
}

const handleConfirmMessage = () => {
  const currentTime = new Date().getTime();
  setItemInSessionStorage('lastRequestTime', currentTime);
};

const RequireTenantGuard: FC<RequireTenantGuardProps> = ({ children }) => {
  const selectedTenant = useRef<Tenant | null>(getItemFromSessionStorage('marketplace'));
  const { openModal } = useModal();

  const handleInactivity = useCallback(() => {
    openModal(
      <ConfirmationModal
        title={`Your selected tenant is ${selectedTenant?.current?.name}, do you want to proceed?`}
        handleConfirm={handleConfirmMessage}
        actionButtonLabel='Ok'
      />
    );
  }, [openModal]);

  const handleTenantChange = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    window.addEventListener(APP_EVENT.INACTIVITY_TIMEOUT, handleInactivity);
    window.addEventListener(APP_EVENT.TENANT_CHANGED, handleTenantChange);

    return () => {
      window.removeEventListener(APP_EVENT.INACTIVITY_TIMEOUT, handleInactivity);
      window.removeEventListener(APP_EVENT.TENANT_CHANGED, handleTenantChange);
    };
  }, [handleInactivity, handleTenantChange]);

  if (!selectedTenant.current) {
    return (
      <Typography
        variant='h2'
        sx={{
          p: 2,
        }}
      >
        To continue please select Tenant
      </Typography>
    );
  }

  return <>{children}</>;
};

export default RequireTenantGuard;
